import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  api,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
  adminHeaders,
} from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast, Toaster } from "sonner";

export default function Admin() {
  const [token, setToken] = useState(getAdminToken());
  const [tab, setTab] = useState("books");

  if (!token) {
    return <AdminLogin onLogin={(t) => setToken(t)} />;
  }

  return (
    <main className="pt-24 md:pt-32 pb-32 px-6 md:px-10 min-h-screen">
      <Toaster theme="dark" position="top-center" />
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between border-b vv-line pb-6">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-ember)]">
              — Editorial Desk
            </div>
            <h1 className="mt-4 font-serif-vv text-5xl md:text-7xl leading-none tracking-tight">
              Admin
            </h1>
          </div>
          <button
            onClick={() => {
              clearAdminToken();
              setToken("");
            }}
            data-testid={ADMIN.logout}
            className="text-xs uppercase tracking-[0.3em] text-[color:var(--vv-bone-dim)] vv-link"
          >
            Sign out
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-6 border-b vv-line pb-3">
          {[
            ["books", "Books", ADMIN.tabBooks],
            ["authors", "Authors", ADMIN.tabAuthors],
            ["posts", "Journal", ADMIN.tabPosts],
            ["newsletter", "Newsletter", ADMIN.tabNewsletter],
          ].map(([k, label, tid]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={tid}
              className={`text-xs uppercase tracking-[0.3em] pb-2 ${
                tab === k
                  ? "text-[color:var(--vv-ember)] border-b border-[color:var(--vv-ember)]"
                  : "text-[color:var(--vv-bone-dim)]"
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={async () => {
              try {
                const r = await api.post("/seed");
                toast.success(
                  `Seeded ${r.data.books} books, ${r.data.authors} authors, ${r.data.posts} posts`
                );
              } catch (e) {
                toast.error("Seed failed");
              }
            }}
            data-testid={ADMIN.seed}
            className="ml-auto text-xs uppercase tracking-[0.3em] text-[color:var(--vv-bone-dim)] vv-link"
          >
            Reseed demo data ↺
          </button>
        </div>

        <div className="mt-12">
          {tab === "books" && <BooksPanel />}
          {tab === "authors" && <AuthorsPanel />}
          {tab === "posts" && <PostsPanel />}
          {tab === "newsletter" && <NewsletterPanel />}
        </div>
      </div>
    </main>
  );
}

function AdminLogin({ onLogin }) {
  const [t, setT] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const r = await api.post("/admin/login", { token: t });
      setAdminToken(r.data.token);
      onLogin(r.data.token);
    } catch {
      setErr("Invalid token");
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md border vv-line p-10 bg-[color:var(--vv-surface)]"
      >
        <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-ember)]">
          — Editorial Desk
        </div>
        <h1 className="mt-4 font-serif-vv text-5xl leading-none">Sign in.</h1>
        <p className="mt-3 text-[color:var(--vv-bone-dim)] text-sm">
          Access is by shared editor token.
        </p>
        <input
          type="password"
          value={t}
          onChange={(e) => setT(e.target.value)}
          placeholder="Editor token"
          data-testid={ADMIN.loginInput}
          className="mt-8 w-full bg-transparent border-b vv-line py-3 outline-none text-lg"
        />
        {err && <div className="mt-3 text-xs text-red-400">{err}</div>}
        <button
          type="submit"
          data-testid={ADMIN.loginSubmit}
          className="mt-8 w-full border vv-line px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-[color:var(--vv-bone)] hover:text-[color:var(--vv-bg)] transition-colors"
        >
          Enter →
        </button>
      </form>
    </main>
  );
}

/* ---------- BOOKS ---------- */
function BooksPanel() {
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [form, setForm] = useState(blankBook());
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    const [b, a] = await Promise.all([api.get("/books"), api.get("/authors")]);
    setBooks(b.data);
    setAuthors(a.data);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const author = authors.find((x) => x.id === form.author_id);
      const payload = { ...form, author_name: author?.name || form.author_name, year: parseInt(form.year, 10) };
      await api.post("/admin/books", payload, adminHeaders());
      toast.success("Book saved");
      setForm(blankBook());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    await api.delete(`/admin/books/${id}`, adminHeaders());
    toast.success("Deleted");
    load();
  };

  const genBlurb = async () => {
    if (!form.title || !form.author_id) {
      toast.error("Title & author required for AI blurb");
      return;
    }
    setAiLoading(true);
    try {
      const author = authors.find((x) => x.id === form.author_id);
      const r = await api.post(
        "/admin/blurb",
        {
          title: form.title,
          author: author?.name || form.author_name,
          genre: form.genre,
          keywords: form.subtitle,
        },
        adminHeaders()
      );
      setForm((f) => ({ ...f, blurb: r.data.blurb }));
      toast.success("Blurb generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
      <form onSubmit={submit} data-testid={ADMIN.bookForm} className="space-y-6">
        <h2 className="font-serif-vv text-3xl">Add / Edit book</h2>
        <Field label="Title">
          <input
            data-testid={ADMIN.bookTitle}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Slug (url)">
          <input
            data-testid={ADMIN.bookSlug}
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Subtitle / motifs">
          <input
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            className={inp}
          />
        </Field>
        <Field label="Author">
          <select
            data-testid={ADMIN.bookAuthor}
            value={form.author_id}
            onChange={(e) => setForm({ ...form, author_id: e.target.value })}
            className={inp}
            required
          >
            <option value="">— Select —</option>
            {authors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-6">
          <Field label="Year">
            <input
              type="number"
              data-testid={ADMIN.bookYear}
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              className={inp}
              required
            />
          </Field>
          <Field label="Pages">
            <input
              type="number"
              value={form.pages}
              onChange={(e) => setForm({ ...form, pages: parseInt(e.target.value || "0", 10) })}
              className={inp}
            />
          </Field>
        </div>
        <Field label="Genre">
          <input
            data-testid={ADMIN.bookGenre}
            value={form.genre}
            onChange={(e) => setForm({ ...form, genre: e.target.value })}
            className={inp}
          />
        </Field>
        <Field label="Cover URL">
          <input
            data-testid={ADMIN.bookCover}
            value={form.cover_url}
            onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Amazon URL">
          <input
            data-testid={ADMIN.bookAmazon}
            value={form.amazon_url}
            onChange={(e) => setForm({ ...form, amazon_url: e.target.value })}
            className={inp}
          />
        </Field>
        <Field label="Bookshop.org URL">
          <input
            data-testid={ADMIN.bookBookshop}
            value={form.bookshop_url}
            onChange={(e) => setForm({ ...form, bookshop_url: e.target.value })}
            className={inp}
          />
        </Field>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            data-testid={ADMIN.bookFeatured}
            checked={form.featured}
            onChange={(e) => setForm({ ...form, featured: e.target.checked })}
          />
          Feature on home
        </label>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={form.coming_soon}
            onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })}
          />
          Coming soon (hides buy links, shows badge)
        </label>

        <Field label="Blurb">
          <textarea
            data-testid={ADMIN.bookBlurb}
            value={form.blurb}
            onChange={(e) => setForm({ ...form, blurb: e.target.value })}
            className={`${inp} min-h-[180px]`}
            required
          />
        </Field>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={genBlurb}
            disabled={aiLoading}
            data-testid={ADMIN.aiBlurb}
            className="text-xs uppercase tracking-[0.3em] border vv-line px-5 py-3 hover:bg-[color:var(--vv-ember)] hover:border-[color:var(--vv-ember)] hover:text-[color:var(--vv-bg)] transition-colors disabled:opacity-50"
          >
            {aiLoading ? "Generating…" : "✦ AI Blurb (Gemini)"}
          </button>
          <button
            type="submit"
            data-testid={ADMIN.bookSubmit}
            className="text-xs uppercase tracking-[0.3em] border vv-line px-5 py-3 hover:bg-[color:var(--vv-bone)] hover:text-[color:var(--vv-bg)] transition-colors"
          >
            Save book →
          </button>
        </div>
      </form>

      <div>
        <h2 className="font-serif-vv text-3xl mb-6">
          Existing titles · {books.length}
        </h2>
        <div className="divide-y vv-line border-y vv-line">
          {books.map((b) => (
            <div key={b.id} className="py-5 flex justify-between items-center gap-6">
              <div>
                <div className="font-serif-vv text-xl">{b.title}</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--vv-bone-dim)] mt-1">
                  {b.author_name} · {b.year}
                </div>
              </div>
              <button
                onClick={() => del(b.id)}
                className="text-xs uppercase tracking-[0.3em] text-red-400 vv-link"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- AUTHORS ---------- */
function AuthorsPanel() {
  const [authors, setAuthors] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", bio: "", portrait_url: "", origin: "" });

  const load = () => api.get("/authors").then((r) => setAuthors(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/authors", form, adminHeaders());
      toast.success("Author added");
      setForm({ name: "", slug: "", bio: "", portrait_url: "", origin: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/authors/${id}`, adminHeaders());
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
      <form onSubmit={submit} className="space-y-6">
        <h2 className="font-serif-vv text-3xl">Add author</h2>
        <Field label="Name">
          <input
            data-testid={ADMIN.authorName}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Slug">
          <input
            data-testid={ADMIN.authorSlug}
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Origin (city · city)">
          <input
            value={form.origin}
            onChange={(e) => setForm({ ...form, origin: e.target.value })}
            className={inp}
          />
        </Field>
        <Field label="Portrait URL">
          <input
            data-testid={ADMIN.authorPortrait}
            value={form.portrait_url}
            onChange={(e) => setForm({ ...form, portrait_url: e.target.value })}
            className={inp}
          />
        </Field>
        <Field label="Bio">
          <textarea
            data-testid={ADMIN.authorBio}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className={`${inp} min-h-[160px]`}
            required
          />
        </Field>
        <button
          data-testid={ADMIN.authorSubmit}
          className="text-xs uppercase tracking-[0.3em] border vv-line px-5 py-3 hover:bg-[color:var(--vv-bone)] hover:text-[color:var(--vv-bg)] transition-colors"
        >
          Add author →
        </button>
      </form>

      <div>
        <h2 className="font-serif-vv text-3xl mb-6">Roster · {authors.length}</h2>
        <div className="divide-y vv-line border-y vv-line">
          {authors.map((a) => (
            <div key={a.id} className="py-5 flex justify-between">
              <div>
                <div className="font-serif-vv text-xl">{a.name}</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--vv-bone-dim)] mt-1">
                  {a.origin}
                </div>
              </div>
              <button
                onClick={() => del(a.id)}
                className="text-xs uppercase tracking-[0.3em] text-red-400 vv-link"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- POSTS ---------- */
function PostsPanel() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", body: "", kicker: "", hero_image: "" });

  const load = () => api.get("/posts").then((r) => setPosts(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/posts", form, adminHeaders());
      toast.success("Post published");
      setForm({ title: "", slug: "", excerpt: "", body: "", kicker: "", hero_image: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/posts/${id}`, adminHeaders());
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
      <form onSubmit={submit} className="space-y-6">
        <h2 className="font-serif-vv text-3xl">New dispatch</h2>
        <Field label="Title">
          <input
            data-testid={ADMIN.postTitle}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Slug">
          <input
            data-testid={ADMIN.postSlug}
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Kicker">
          <input
            data-testid={ADMIN.postKicker}
            value={form.kicker}
            onChange={(e) => setForm({ ...form, kicker: e.target.value })}
            className={inp}
          />
        </Field>
        <Field label="Excerpt">
          <input
            data-testid={ADMIN.postExcerpt}
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            className={inp}
            required
          />
        </Field>
        <Field label="Hero image URL">
          <input
            data-testid={ADMIN.postHero}
            value={form.hero_image}
            onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
            className={inp}
          />
        </Field>
        <Field label="Body">
          <textarea
            data-testid={ADMIN.postBody}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className={`${inp} min-h-[220px]`}
            required
          />
        </Field>
        <button
          data-testid={ADMIN.postSubmit}
          className="text-xs uppercase tracking-[0.3em] border vv-line px-5 py-3 hover:bg-[color:var(--vv-bone)] hover:text-[color:var(--vv-bg)] transition-colors"
        >
          Publish →
        </button>
      </form>

      <div>
        <h2 className="font-serif-vv text-3xl mb-6">Posts · {posts.length}</h2>
        <div className="divide-y vv-line border-y vv-line">
          {posts.map((p) => (
            <div key={p.id} className="py-5 flex justify-between">
              <div>
                <div className="font-serif-vv text-xl">{p.title}</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--vv-bone-dim)] mt-1">
                  {p.kicker}
                </div>
              </div>
              <button
                onClick={() => del(p.id)}
                className="text-xs uppercase tracking-[0.3em] text-red-400 vv-link"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- NEWSLETTER ---------- */
function NewsletterPanel() {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/admin/newsletter", adminHeaders()).then((r) => setList(r.data));
  }, []);
  return (
    <div>
      <h2 className="font-serif-vv text-3xl mb-6">Enrollees · {list.length}</h2>
      <div className="divide-y vv-line border-y vv-line">
        {list.map((n) => (
          <div key={n.id} className="py-4 flex justify-between text-sm">
            <span>{n.email}</span>
            <span className="text-[color:var(--vv-bone-dim)] text-xs uppercase tracking-widest">
              {n.created_at?.slice(0, 10)}
            </span>
          </div>
        ))}
        {list.length === 0 && (
          <div className="py-6 text-[color:var(--vv-bone-dim)] text-sm">
            No enrollments yet.
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
const inp =
  "w-full bg-transparent border-b vv-line py-2 outline-none text-base focus:border-[color:var(--vv-ember)] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--vv-bone-dim)] mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

function blankBook() {
  return {
    title: "",
    slug: "",
    subtitle: "",
    author_id: "",
    author_name: "",
    year: new Date().getFullYear(),
    pages: 0,
    genre: "",
    blurb: "",
    cover_url: "",
    amazon_url: "",
    bookshop_url: "",
    featured: false,
    coming_soon: false,
  };
}
